import React, { useState } from 'react';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { PASSWORD_RULES, isPasswordValid } from '../../utils/passwordValidation.js';
import { EMAIL_FORMAT_ERROR_MESSAGE, isEmailFormatAcceptable } from '../../utils/emailValidation.js';
import api from '../../utils/api.js';
import { handleApiError } from '../../utils/errorHandler.js';
import { useTenant } from '../../contexts/TenantContext.js';
import './UserFormModal.css';

/**
 * ユーザー作成/編集兼用モーダル
 *
 * @param {Object} props
 * @param {Object|null} props.editingUser - 編集対象ユーザー（nullなら作成モード）
 * @param {Function} props.onClose - モーダルを閉じるコールバック
 * @param {Function} props.onSaved - 保存成功時のコールバック
 */
function UserFormModal({ editingUser, onClose, onSaved }) {
    const isEditMode = !!editingUser;
    const { tenantSlug } = useTenant();

    // 親側が {createModal && <UserFormModal />} で条件付きマウントするため、
    // マウント中は常にスクロールをロックする（isOpen prop は持たない設計）
    useBodyScrollLock(true);

    // フォーム状態
    const [userName, setUserName] = useState(isEditMode ? editingUser.user_name : '');
    const [userEmail, setUserEmail] = useState(isEditMode ? editingUser.user_email : '');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [isAdmin, setIsAdmin] = useState(isEditMode ? editingUser.is_admin : false);
    const [hasAiAccess, setHasAiAccess] = useState(isEditMode ? editingUser.has_ai_access : true);
    const [isActive, setIsActive] = useState(isEditMode ? editingUser.is_active : true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (isEditMode) {
            await handleEdit();
        } else {
            await handleCreate();
        }
    };

    const handleCreate = async () => {
        // ブラウザの type="email" は a@b を通すが、サーバは 422（英語の検査の文言）を返すため、送る前に止める
        if (!isEmailFormatAcceptable(userEmail)) {
            setError(EMAIL_FORMAT_ERROR_MESSAGE);
            return;
        }
        if (!isPasswordValid(password)) {
            setError('パスワードは8文字以上で、英字と数字をそれぞれ1文字以上含めてください。');
            return;
        }
        if (password !== passwordConfirm) {
            setError('パスワードが一致しません。');
            return;
        }

        setLoading(true);
        try {
            const response = await api.fetch('/auth/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_name: userName,
                    user_email: userEmail,
                    password: password,
                    tenant_slug: tenantSlug,
                    is_admin: isAdmin,
                    has_ai_access: hasAiAccess,
                }),
            });

            if (response && response.ok) {
                onSaved();
            } else {
                const errorMessage = await handleApiError(response, 'ユーザーの作成に失敗しました。');
                setError(errorMessage);
            }
        } catch (err) {
            setError('通信エラーが発生しました。');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async () => {
        setLoading(true);
        try {
            const response = await api.fetch(`/users/${editingUser.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_name: userName,
                    is_admin: isAdmin,
                    has_ai_access: hasAiAccess,
                    is_active: isActive,
                }),
            });

            if (response && response.ok) {
                onSaved();
            } else {
                const errorMessage = await handleApiError(response, 'ユーザーの更新に失敗しました。');
                setError(errorMessage);
            }
        } catch (err) {
            setError('通信エラーが発生しました。');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <h3 className="modal-title">
                    {isEditMode ? 'ユーザー編集' : '新規ユーザー作成'}
                </h3>
                <form onSubmit={handleSubmit}>
                    {/* 氏名 */}
                    <div className="auth-form-group">
                        <label className="auth-label">氏名 *</label>
                        <input
                            type="text"
                            className="auth-input"
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            required
                            placeholder="山田 太郎"
                        />
                    </div>

                    {/* メールアドレス */}
                    {isEditMode ? (
                        <div className="auth-form-group">
                            <label className="auth-label">メールアドレス</label>
                            <input
                                type="email"
                                className="auth-input input-readonly"
                                value={userEmail}
                                disabled
                            />
                        </div>
                    ) : (
                        <div className="auth-form-group">
                            <label className="auth-label">メールアドレス *</label>
                            <input
                                type="email"
                                className="auth-input"
                                value={userEmail}
                                onChange={(e) => setUserEmail(e.target.value)}
                                required
                                placeholder="example@email.com"
                            />
                        </div>
                    )}

                    {/* パスワード（作成時のみ） */}
                    {!isEditMode && (
                        <>
                            <div className="auth-form-group">
                                <label className="auth-label">パスワード *</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="auth-input"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        placeholder="パスワード"
                                        style={{ paddingRight: '40px' }}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-button"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? '隠す' : '表示'}
                                    </button>
                                </div>
                                {password && (
                                    <div className="password-rules">
                                        {PASSWORD_RULES.map((rule, i) => (
                                            <small key={i} className={rule.test(password) ? 'rule-pass' : 'rule-fail'}>
                                                {rule.test(password) ? '✓' : '✗'} {rule.label}
                                            </small>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="auth-form-group">
                                <label className="auth-label">パスワード（確認用） *</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showPasswordConfirm ? 'text' : 'password'}
                                        className="auth-input"
                                        value={passwordConfirm}
                                        onChange={(e) => setPasswordConfirm(e.target.value)}
                                        required
                                        placeholder="パスワード（確認用）"
                                        style={{ paddingRight: '40px' }}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-button"
                                        onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                                    >
                                        {showPasswordConfirm ? '隠す' : '表示'}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {/* 権限 */}
                    <div className="auth-form-group">
                        <label className="auth-label">権限</label>
                        <div className="segment-group">
                            <button type="button"
                                className={`segment-button ${!isAdmin ? 'active green' : ''}`}
                                onClick={() => setIsAdmin(false)}>
                                一般
                            </button>
                            <button type="button"
                                className={`segment-button ${isAdmin ? 'active blue' : ''}`}
                                onClick={() => setIsAdmin(true)}>
                                管理者
                            </button>
                        </div>
                    </div>

                    {/* AI機能利用 */}
                    <div className="auth-form-group">
                        <label className="auth-label">AI機能利用</label>
                        <div className="segment-group">
                            <button type="button"
                                className={`segment-button ${hasAiAccess ? 'active green' : ''}`}
                                onClick={() => setHasAiAccess(true)}>
                                利用可
                            </button>
                            <button type="button"
                                className={`segment-button ${!hasAiAccess ? 'active red' : ''}`}
                                onClick={() => setHasAiAccess(false)}>
                                利用不可
                            </button>
                        </div>
                    </div>

                    {/* アカウント状態（編集時のみ） */}
                    {isEditMode && (
                        <div className="auth-form-group">
                            <label className="auth-label">アカウント状態</label>
                            <div className="segment-group">
                                <button type="button"
                                    className={`segment-button ${isActive ? 'active green' : ''}`}
                                    onClick={() => setIsActive(true)}>
                                    有効
                                </button>
                                <button type="button"
                                    className={`segment-button ${!isActive ? 'active red' : ''}`}
                                    onClick={() => setIsActive(false)}>
                                    無効
                                </button>
                            </div>
                        </div>
                    )}

                    {/* エラーメッセージ */}
                    {error && (
                        <div className="auth-error">{error}</div>
                    )}

                    {/* アクションボタン */}
                    <div className="modal-actions">
                        <button type="button" className="modal-button-cancel" onClick={onClose} disabled={loading}>
                            キャンセル
                        </button>
                        <button type="submit" className="modal-button-confirm"
                            disabled={loading || (!isEditMode && !isPasswordValid(password))}>
                            {loading ? '処理中...' : (isEditMode ? '保存' : 'ユーザーを作成')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default UserFormModal;
